import React, { useEffect, useState } from 'react';
import { useFeathers } from '../../app/util';
import { SearchableSelectInput, CTAButton } from '../index';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

const EditBallotOverview = ({
  election,
  onSave,
  saving = false
}) => {
  const feathers = useFeathers();
  const electionKey = election?.key;
  const stateId = election?.state;

  const [selectedOffices, setSelectedOffices] = useState([]);
  const [optionsForField, setOptionsForField] = useState([]);
  const [loadingForField, setLoadingForField] = useState(false);
  const [queuedResultsForField, setQueuedResultsForField] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const searchForOffices = async (searchText) => {
    if (!feathers) return;

    const timestamp = Date.now();
    setLoadingForField(true);

    const query = {
      ...(searchText ? { name: { $search: searchText } } : {}),
      ...(stateId ? { district: stateId } : {}),
    };

    const offices = await feathers.getService('offices').find({ query });
    const options = offices.data.map(({ name, key, _id, ...rest }) => ({
      text: name,
      key,
      name,
      _id,
      ...rest,
    }));

    setQueuedResultsForField({ timestamp, options });
    setLoadingForField(false);
  };

  useEffect(() => {
    if (queuedResultsForField) {
      setOptionsForField(queuedResultsForField.options);
      setQueuedResultsForField(null);
    }
  }, [queuedResultsForField]);

  useEffect(() => {
    const loadInitialOffices = async () => {
      const keys = election?.ballotOverview || [];
      if (keys.length === 0) {
        setSelectedOffices([]);
        setLoadingInitial(false);
        return;
      }

      if (!feathers) {
        setLoadingInitial(false);
        return;
      }

      try {
        const offices = await feathers.getService('offices').find({
          query: { key: keys, $limit: keys.length },
        });
        const value = offices.data.map(({ name, key, _id }) => ({
          text: name,
          key,
          name,
          _id,
        }));
        setSelectedOffices(value);
      } catch (err) {
        console.error('Failed to load ballot overview offices', err);
        setSelectedOffices([]);
      } finally {
        setLoadingInitial(false);
      }
    };

    loadInitialOffices();
  }, [election?.ballotOverview, feathers]);

  const handleChange = (e) => {
    const newValue = e?.target?.value;
    if (Array.isArray(newValue)) {
      setSelectedOffices(newValue);
    }
  };

  const handleSave = async () => {
    const ballotOverview = selectedOffices.map((o) => o.key);
    await onSave(ballotOverview);
  };

  const hasChanges =
    JSON.stringify(selectedOffices.map((o) => o.key).sort()) !==
    JSON.stringify((election?.ballotOverview || []).sort());

  const handleContinue = async () => {
    await onSave(selectedOffices.map((o) => o.key));
  };

  if (loadingInitial) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        Loading...
      </Typography>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        Ballot overview
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        Select key offices to show on the summary page when this election is in prep. These will appear as a preview of what will be on the ballot.
      </Typography>
      <SearchableSelectInput
        name="ballotOverview"
        multi={true}
        onChangeSearchText={searchForOffices}
        value={selectedOffices}
        onChange={handleChange}
        optionsLoading={loadingForField}
        options={optionsForField}
        placeholder="Search offices..."
        clearAfterSelect={true}
      />
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <CTAButton
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          Save
        </CTAButton>
        <Button
          variant="outlined"
          onClick={handleContinue}
          disabled={saving}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export { EditBallotOverview };
